import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconUpload,
  IconCircleMinus,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypePng,
  IconFileTypeJpg,
  IconFile,
  IconAlertSquareRoundedFilled,
} from "@tabler/icons-react";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useDocument } from "@/hooks/useDocument";
import { DocumentSource } from "@/types/document.dto";
import { addUploadProgressDocs } from "@/utils/upload-progress-storage";
import MHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import styles from "./document.module.css";
import { MAX_FILE_SIZE } from "@/utils/constants";
import { getMemberInfo } from "@/hooks/useMembers";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const UploadReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const currentMemberInfo = getMemberInfo();

  // Get memberId from localStorage, fallback to location.state
  const getMemberId = (): string | undefined => {
    try {
      const memberRaw =
        localStorage.getItem("selectedMember") ||
        localStorage.getItem("member");
      if (memberRaw) {
        const member = JSON.parse(memberRaw);
        return member?.id || member?._id;
      }
    } catch (e) {
      console.error("Error getting member ID:", e);
    }
  };

  const memberId = getMemberId();
  const { uploadDocument, getDocumentTypeCounts } = useDocument(memberId || "");

  const [selectedFiles, setSelectedFiles] = useState<File[]>(
    location.state?.files || []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [fileToRemove, setFileToRemove] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const memberInfo = location.state?.memberInfo || {};
  const backUrl = location.state?.backUrl || "/documents";

  // Fetch document count on mount - same pattern as DocumentsLanding
  useEffect(() => {
    const fetchCounts = async () => {
      if (memberId) {
        try {
          const counts = await getDocumentTypeCounts({ mode: "landing" });
          setTotalCount((counts as any)?.totalDocuments ?? 0);
        } catch (error) {
          console.error("Error fetching document counts:", error);
        }
      }
    };
    fetchCounts();
  }, [memberId]);

  // Track page load
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo: currentMemberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
      screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_FILES_CONFIRM,
    });
  }, []);

  const allowedExtensions = ["pdf", "jpg", "jpeg", "png", "heic", "heif"];
  const hasInvalidFileType = selectedFiles.some(
    (file) =>
      !allowedExtensions.includes(
        file.name.split(".").pop()?.toLowerCase() || ""
      )
  );
  const hasOversizedFile = selectedFiles.some(
    (file) => file.size > MAX_FILE_SIZE
  );

  const confirmRemoveFile = () => {
    if (fileToRemove === null) return;
    const newFiles = [...selectedFiles];
    newFiles.splice(fileToRemove, 1);
    setSelectedFiles(newFiles);
    setFileToRemove(null);

    if (newFiles.length === 0) {
      navigate(backUrl);
    }
  };

  const cancelRemoveFile = () => setFileToRemove(null);

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || hasOversizedFile || hasInvalidFileType)
      return;

    setIsUploading(true);
    const createdDocumentIds: string[] = [];

    try {
      // Get the upload source from location.state (set in MDocumentUploadOptions)
      const uploadSource = location.state?.uploadSource as
        | DocumentSource
        | undefined;

      // Track first upload if totalCount is 0 - same pattern as DocumentsLanding
      if (totalCount === 0) {
        trackUserDataLoaded({
          memberInfo: currentMemberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_FILES_CONFIRM,
          action: "first_upload",
          additionalUserProperties: {
            document_count: String(selectedFiles.length),
          },
        });
      }

      // Track milestone: 10 documents uploaded
      const newTotalCount = totalCount + selectedFiles.length;
      if (totalCount < 10 && newTotalCount >= 10) {
        trackUserDataLoaded({
          memberInfo: currentMemberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_FILES_CONFIRM,
          action: "10_documents_uploaded",
        });
      }

      const duplicateNames: string[] = [];
      let failedCount = 0;

      for (const file of selectedFiles) {
        try {
          const result = (await uploadDocument(
            file,
            uploadSource || DocumentSource.APP
          )) as any;
          if (result?._id || result?.id) {
            createdDocumentIds.push(result._id || result.id);
          }
        } catch (e: any) {
          if (e?.response?.status === 409) {
            duplicateNames.push(
              e?.response?.data?.message?.fileName || "Unknown"
            );
          } else {
            failedCount++;
          }
        }
      }

      // Store document IDs in localStorage for upload progress tracking
      if (createdDocumentIds.length > 0) {
        addUploadProgressDocs(createdDocumentIds);
      }

      if (duplicateNames.length > 0) {
        showToast({
          message: `${
            duplicateNames.length
          } file(s) already uploaded: ${duplicateNames.join(", ")}`,
          variant: "error",
        });
      }
      if (failedCount > 0) {
        showToast({
          message: `${failedCount} file(s) failed to upload. Please try again.`,
          variant: "error",
        });
      }

      setSelectedFiles([]);
      localStorage.removeItem("uploadSource");

      navigate("/documents");
    } catch (error) {
      showToast({ message: "Please try again.", variant: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    navigate(backUrl);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        padding: 24,
        marginTop: -45,
      }}
    >
      <MHeader
        module=""
        memberInfo={memberInfo}
        title="Confirm your upload"
        description="Review your files before uploading. Delete any file marked with an alert to continue."
        learnMoreContent={
          <>
            Uploaded files become part of your secure health profile. Review
            your selected files and delete any unwanted or flagged documents
            before uploading.
            <br />
            <br />
            Maatra supports PDF, JPG, JPEG, and PNG files up to 20 MB each.
          </>
        }
        drawerTitle="More Info"
      />
      <MSpacer mt={20} />

      <div className="flex flex-col gap-4">
        <div className="text-xs font-semibold text-gray-600">
          Total upload size:{" "}
          {(
            selectedFiles.reduce((total, file) => total + file.size, 0) /
            (1024 * 1024)
          ).toFixed(2)}{" "}
          MB
        </div>

        <div
          className="overflow-y-auto flex flex-col gap-[12px]"
          style={{ maxHeight: "calc(85vh - 280px)", minHeight: "100px" }}
        >
          {selectedFiles.map((file, index) => {
            const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
            const isPdf = fileExt === "pdf";
            const isDoc = ["doc", "docx", "odt"].includes(fileExt);
            const fileSize =
              file.size > 1024 * 1024
                ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                : `${(file.size / 1024).toFixed(1)} KB`;
            const isTooLarge = file.size > MAX_FILE_SIZE;
            const isInvalidType = !allowedExtensions.includes(fileExt);

            return (
              <div
                key={index}
                className="flex flex-col p-3 hover:bg-gray-50 rounded-lg border border-gray-100"
              >
                {/* File Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={styles.fileIconContainer}>
                      {isPdf ? (
                        <IconFileTypePdf
                          size={24}
                          className={styles.fileIcon}
                        />
                      ) : isDoc ? (
                        <IconFileTypeDoc
                          size={24}
                          className={styles.fileIcon}
                        />
                      ) : fileExt === "png" ? (
                        <IconFileTypePng
                          size={24}
                          className={styles.fileIcon}
                        />
                      ) : fileExt === "jpg" || fileExt === "jpeg" ? (
                        <IconFileTypeJpg
                          size={24}
                          className={styles.fileIcon}
                        />
                      ) : (
                        <IconFile size={24} className={styles.fileIcon} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="body-base-semibold default-text-color truncate">
                        {file.name}
                      </p>
                      <p className="text-xs font-medium text-gray-500 mt-0.5">
                        {fileSize}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (isInvalidType || isTooLarge) {
                        const newFiles = [...selectedFiles];
                        newFiles.splice(index, 1);
                        setSelectedFiles(newFiles);

                        if (newFiles.length === 0) {
                          navigate(backUrl);
                        }
                        return;
                      }
                      setFileToRemove(index);
                    }}
                    className="text-[#983145] hover:opacity-80 p-1.5 rounded-full hover:bg-gray-100 transition-opacity flex-shrink-0"
                    disabled={isUploading}
                    aria-label="Remove file"
                  >
                    <IconCircleMinus size={20} />
                  </button>

                  <MDialog
                    isOpen={fileToRemove === index}
                    onClose={cancelRemoveFile}
                    title="Remove this file ?"
                    description="You'll need to select it again for upload."
                    primaryCtaText="Yes"
                    onPrimaryCta={confirmRemoveFile}
                    secondaryCtaText="No"
                    onSecondaryCta={cancelRemoveFile}
                  />
                </div>

                {/* Error messages */}
                {isTooLarge && (
                  <div
                    className={`flex items-center ${styles.uncategorizedOcrErrorRow} pt-[10px]`}
                  >
                    <IconAlertSquareRoundedFilled
                      className={styles.uncategorizedOcrErrorIcon}
                      size={16}
                    />
                    <p className={styles.uncategorizedOcrError}>
                      This file exceeds 20 MB.
                    </p>
                  </div>
                )}
                {isInvalidType && (
                  <div
                    className={`flex items-center ${styles.uncategorizedOcrErrorRow} pt-[10px]`}
                  >
                    <IconAlertSquareRoundedFilled
                      className={styles.uncategorizedOcrErrorIcon}
                      size={16}
                    />
                    <p className={styles.uncategorizedOcrError}>
                      This format isn't supported.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="confirm-footer">
          <MButton
            variant="outline"
            size="md"
            onClick={handleCancel}
            disabled={isUploading}
            className="flex-1 w-[48%]"
          >
            Cancel
          </MButton>

          <MButton
            variant="filled"
            onClick={handleUpload}
            size="md"
            leftIcon={!isUploading ? <IconUpload size={16} /> : undefined}
            className="flex-1 w-[48%]"
            disabled={isUploading || hasOversizedFile || hasInvalidFileType}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </MButton>
        </div>
      </div>
    </div>
  );
};

export default UploadReviewPage;
